import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { router } from 'expo-router';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react-native';
import { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  GARAGE_PHOTO_LIMIT,
  MAX_GARAGE_MODS,
  MAX_GARAGE_STORY_LENGTH,
  VEHICLE_OWNERSHIP_STATUSES,
  VEHICLE_TYPES,
  createGarageSharePost,
  garageVehiclePath,
  newGarageVehicleId,
  uploadGaragePhoto,
  type GarageMod,
  type GarageVehicle,
  type GarageVehiclePhoto,
  type VehicleOwnershipStatus,
  type VehiclePhotoUpload,
  type VehicleType,
} from '@/src/features/garage/garage-service';
import {
  useCreateGarageVehicle,
  useUpdateGarageVehicle,
} from '@/src/features/garage/use-garage';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { UserFacingError } from '@/src/utils/errors';

type LocalPhoto = VehiclePhotoUpload & {
  localURI?: string | null;
  uploaded?: boolean;
};

function photoFromExisting(photo: GarageVehiclePhoto): LocalPhoto {
  return {
    clientId: photo.id,
    contentType: photo.contentType,
    downloadURL: photo.downloadURL,
    height: photo.height,
    localURI: null,
    order: photo.order,
    sizeBytes: photo.sizeBytes,
    storagePath: photo.storagePath,
    uploaded: true,
    width: photo.width,
  };
}

function initialPhotosFromExisting(photos: GarageVehiclePhoto[]) {
  const localPhotos = photos.map(photoFromExisting);
  const coverIndex = localPhotos.findIndex((photo) =>
    photos.some(
      (existingPhoto) =>
        existingPhoto.id === photo.clientId && existingPhoto.isCover,
    ),
  );

  if (coverIndex <= 0) {
    return localPhotos;
  }

  return [
    localPhotos[coverIndex],
    ...localPhotos.slice(0, coverIndex),
    ...localPhotos.slice(coverIndex + 1),
  ];
}

function cleanNullable(value: string) {
  return value.trim() || null;
}

function selectableLabel(value: string) {
  return value
    .split('_')
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');
}

export function GarageVehicleForm({
  existingPhotos = [],
  vehicle,
}: {
  existingPhotos?: GarageVehiclePhoto[];
  vehicle?: GarageVehicle | null;
}) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const createVehicle = useCreateGarageVehicle();
  const updateVehicle = useUpdateGarageVehicle();
  const [vehicleId] = useState(vehicle?.id ?? newGarageVehicleId());
  const [type, setType] = useState<VehicleType>(vehicle?.type ?? 'car');
  const [ownershipStatus, setOwnershipStatus] =
    useState<VehicleOwnershipStatus>(vehicle?.ownershipStatus ?? 'current');
  const [nickname, setNickname] = useState(vehicle?.nickname ?? '');
  const [year, setYear] = useState(vehicle?.year ? String(vehicle.year) : '');
  const [make, setMake] = useState(vehicle?.make ?? '');
  const [model, setModel] = useState(vehicle?.model ?? '');
  const [trim, setTrim] = useState(vehicle?.trim ?? '');
  const [story, setStory] = useState(vehicle?.story ?? '');
  const [mods, setMods] = useState<GarageMod[]>(vehicle?.mods ?? []);
  const [photos, setPhotos] = useState<LocalPhoto[]>(
    initialPhotosFromExisting(existingPhotos),
  );
  const [shareToFeed, setShareToFeed] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const saving = createVehicle.isPending || updateVehicle.isPending;
  const yearNumber = year.trim() ? Number(year.trim()) : null;
  const orderedPhotos = useMemo(
    () =>
      photos.map((photo, index) => ({
        ...photo,
        order: index,
      })),
    [photos],
  );

  async function pickPhotos() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    const remaining = GARAGE_PHOTO_LIMIT - photos.length;

    if (remaining <= 0) {
      showError(
        new Error(
          `Garage vehicles can have up to ${GARAGE_PHOTO_LIMIT} photos.`,
        ),
      );
      return;
    }

    try {
      const permission =
        await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!permission.granted) {
        throw new Error('Photo library permission is required.');
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        allowsMultipleSelection: true,
        mediaTypes: ['images'],
        quality: 0.9,
        selectionLimit: remaining,
      });

      if (result.canceled) {
        return;
      }

      const newPhotos: LocalPhoto[] = [];

      for (const [index, asset] of result.assets
        .slice(0, remaining)
        .entries()) {
        const clientId = `${Date.now()}-${index}-${Math.random()
          .toString(36)
          .slice(2, 8)}`;

        setUploadStatus(
          `Uploading photo ${index + 1} of ${result.assets.length}...`,
        );
        const uploaded = await uploadGaragePhoto({
          asset,
          clientId,
          order: photos.length + index,
          uid: user.uid,
          vehicleId,
          onUploadProgress: (progress) => {
            setUploadStatus(
              `Uploading photo ${index + 1} of ${result.assets.length} ${Math.round(
                progress * 100,
              )}%`,
            );
          },
        });

        newPhotos.push({
          ...uploaded,
          localURI: asset.uri,
          uploaded: true,
        });
      }

      setPhotos((currentPhotos) => [...currentPhotos, ...newPhotos]);
    } catch (error) {
      showError(
        error,
        'Unable to upload that vehicle photo. Try a JPEG, PNG, WebP, or iPhone photo under 200MB.',
      );
    } finally {
      setUploadStatus(null);
    }
  }

  function movePhoto(index: number, direction: -1 | 1) {
    setPhotos((currentPhotos) => {
      const nextPhotos = [...currentPhotos];
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= nextPhotos.length) {
        return currentPhotos;
      }

      const [photo] = nextPhotos.splice(index, 1);
      nextPhotos.splice(targetIndex, 0, photo);
      return nextPhotos;
    });
  }

  function removePhoto(clientId: string) {
    setPhotos((currentPhotos) =>
      currentPhotos.filter((photo) => photo.clientId !== clientId),
    );
  }

  function addMod() {
    if (mods.length >= MAX_GARAGE_MODS) {
      return;
    }

    setMods((currentMods) => [
      ...currentMods,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        category: '',
        title: '',
        brandPart: '',
        notes: '',
      },
    ]);
  }

  function updateMod(index: number, patch: Partial<GarageMod>) {
    setMods((currentMods) =>
      currentMods.map((mod, modIndex) =>
        modIndex === index ? { ...mod, ...patch } : mod,
      ),
    );
  }

  function removeMod(index: number) {
    setMods((currentMods) =>
      currentMods.filter((_mod, modIndex) => modIndex !== index),
    );
  }

  async function saveVehicle() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    const cleanedMake = make.trim();
    const cleanedModel = model.trim();

    if (!cleanedMake || !cleanedModel) {
      showError(new UserFacingError('Add a vehicle make and model.'));
      return;
    }

    try {
      const input = {
        coverPhotoId: orderedPhotos[0]?.clientId ?? null,
        make: cleanedMake,
        model: cleanedModel,
        mods: mods
          .map((mod) => ({
            ...mod,
            category: cleanNullable(mod.category ?? ''),
            title: mod.title.trim(),
            brandPart: cleanNullable(mod.brandPart ?? ''),
            notes: cleanNullable(mod.notes ?? ''),
          }))
          .filter((mod) => mod.title),
        nickname: cleanNullable(nickname),
        ownershipStatus,
        photos: orderedPhotos,
        story: cleanNullable(story),
        trim: cleanNullable(trim),
        type,
        vehicleId,
        year: yearNumber,
      };
      const result = vehicle
        ? await updateVehicle.mutateAsync({ ...input, vehicleId: vehicle.id })
        : await createVehicle.mutateAsync(input);

      if (shareToFeed) {
        await createGarageSharePost(result.vehicleId);
      }

      trackEvent(vehicle ? 'garage_vehicle_update' : 'garage_vehicle_create', {
        has_mods: input.mods.length > 0,
        has_photos: input.photos.length > 0,
        has_story: Boolean(input.story),
        photo_count: input.photos.length,
        share_to_feed: shareToFeed,
        vehicle_type: input.type,
      });

      showSuccess(
        vehicle ? 'Vehicle updated.' : 'Vehicle added to your Garage.',
        'Garage saved',
      );
      router.replace(result.path);
    } catch (error) {
      showError(error, 'Unable to save that Garage vehicle.');
    }
  }

  return (
    <View style={styles.content}>
      <View style={styles.section}>
        <Text style={styles.title}>
          {vehicle ? 'Edit vehicle' : 'Add vehicle'}
        </Text>
        <Text style={styles.helperText}>
          Show your road vehicles, builds, story, and mods. Hide sensitive
          details like license plates before uploading photos.
        </Text>
      </View>

      <View style={styles.segmentGroup}>
        <Text style={styles.label}>Vehicle type</Text>
        <View style={styles.segmentRow}>
          {VEHICLE_TYPES.map((option) => (
            <Pressable
              key={option}
              accessibilityRole="button"
              onPress={() => setType(option)}
              style={[
                styles.segment,
                type === option ? styles.segmentActive : null,
              ]}
            >
              <Text
                style={[
                  styles.segmentText,
                  type === option ? styles.segmentTextActive : null,
                ]}
              >
                {selectableLabel(option)}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={styles.segmentGroup}>
        <Text style={styles.label}>Ownership</Text>
        <View style={styles.segmentRow}>
          {VEHICLE_OWNERSHIP_STATUSES.map((option) => (
            <Pressable
              key={option}
              accessibilityRole="button"
              onPress={() => setOwnershipStatus(option)}
              style={[
                styles.segment,
                ownershipStatus === option ? styles.segmentActive : null,
              ]}
            >
              <Text
                style={[
                  styles.segmentText,
                  ownershipStatus === option ? styles.segmentTextActive : null,
                ]}
              >
                {selectableLabel(option)}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={styles.formGrid}>
        <View style={styles.field}>
          <Text style={styles.label}>Nickname</Text>
          <TextInput
            maxLength={80}
            onChangeText={setNickname}
            placeholder="Optional"
            placeholderTextColor={colors.mutedText}
            style={styles.input}
            value={nickname}
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Year</Text>
          <TextInput
            keyboardType="number-pad"
            maxLength={4}
            onChangeText={setYear}
            placeholder="2024"
            placeholderTextColor={colors.mutedText}
            style={styles.input}
            value={year}
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Make</Text>
          <TextInput
            maxLength={80}
            onChangeText={setMake}
            placeholder="Ford"
            placeholderTextColor={colors.mutedText}
            style={styles.input}
            value={make}
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Model</Text>
          <TextInput
            maxLength={120}
            onChangeText={setModel}
            placeholder="Bronco"
            placeholderTextColor={colors.mutedText}
            style={styles.input}
            value={model}
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Trim</Text>
          <TextInput
            maxLength={120}
            onChangeText={setTrim}
            placeholder="Optional"
            placeholderTextColor={colors.mutedText}
            style={styles.input}
            value={trim}
          />
        </View>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View>
            <Text style={styles.sectionTitle}>Photos</Text>
            <Text style={styles.helperText}>
              Add up to {GARAGE_PHOTO_LIMIT} photos. The first photo is the
              cover, so use the arrows to reorder.
            </Text>
          </View>
          <Button
            title="Add photos"
            variant="secondary"
            disabled={
              photos.length >= GARAGE_PHOTO_LIMIT || Boolean(uploadStatus)
            }
            onPress={() => {
              void pickPhotos();
            }}
            style={styles.sectionHeaderButton}
          />
        </View>
        {uploadStatus ? (
          <View style={styles.uploadStatus}>
            <ActivityIndicator color={colors.primary} />
            <Text style={styles.uploadStatusText}>{uploadStatus}</Text>
          </View>
        ) : null}
        <View style={styles.photoGrid}>
          {orderedPhotos.map((photo, index) => {
            const selected = index === 0;
            const imageURL = photo.localURI ?? photo.downloadURL;

            return (
              <View key={photo.clientId} style={styles.photoCard}>
                <View
                  style={[
                    styles.photoFrame,
                    selected ? styles.photoFrameSelected : null,
                  ]}
                >
                  <Image
                    accessibilityLabel="Vehicle photo"
                    contentFit="cover"
                    source={{ uri: imageURL }}
                    style={styles.photoImage}
                  />
                  {selected ? (
                    <View style={styles.coverBadge}>
                      <Text style={styles.coverBadgeText}>Cover</Text>
                    </View>
                  ) : null}
                </View>
                <View style={styles.photoActions}>
                  <Pressable
                    accessibilityLabel="Move photo up"
                    disabled={index === 0}
                    onPress={() => movePhoto(index, -1)}
                    style={styles.iconButton}
                  >
                    <ArrowUp color={colors.secondary} size={18} />
                  </Pressable>
                  <Pressable
                    accessibilityLabel="Move photo down"
                    disabled={index === orderedPhotos.length - 1}
                    onPress={() => movePhoto(index, 1)}
                    style={styles.iconButton}
                  >
                    <ArrowDown color={colors.secondary} size={18} />
                  </Pressable>
                  <Pressable
                    accessibilityLabel="Remove photo"
                    onPress={() => removePhoto(photo.clientId)}
                    style={styles.iconButton}
                  >
                    <Trash2 color={colors.danger} size={18} />
                  </Pressable>
                </View>
              </View>
            );
          })}
        </View>
      </View>

      <View style={styles.field}>
        <Text style={styles.label}>Story</Text>
        <TextInput
          maxLength={MAX_GARAGE_STORY_LENGTH}
          multiline
          onChangeText={setStory}
          placeholder="What makes this vehicle yours?"
          placeholderTextColor={colors.mutedText}
          style={[styles.input, styles.textArea]}
          textAlignVertical="top"
          value={story}
        />
        <Text style={styles.countText}>
          {story.length}/{MAX_GARAGE_STORY_LENGTH}
        </Text>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Mods</Text>
          <Button
            title="Add mod"
            variant="secondary"
            disabled={mods.length >= MAX_GARAGE_MODS}
            onPress={addMod}
            style={styles.sectionHeaderButton}
          />
        </View>
        {mods.map((mod, index) => (
          <View key={mod.id} style={styles.modCard}>
            <TextInput
              maxLength={120}
              onChangeText={(value) => updateMod(index, { title: value })}
              placeholder="Mod name"
              placeholderTextColor={colors.mutedText}
              style={styles.input}
              value={mod.title}
            />
            <TextInput
              maxLength={80}
              onChangeText={(value) => updateMod(index, { category: value })}
              placeholder="Category"
              placeholderTextColor={colors.mutedText}
              style={styles.input}
              value={mod.category ?? ''}
            />
            <TextInput
              maxLength={120}
              onChangeText={(value) => updateMod(index, { brandPart: value })}
              placeholder="Brand or part"
              placeholderTextColor={colors.mutedText}
              style={styles.input}
              value={mod.brandPart ?? ''}
            />
            <TextInput
              maxLength={500}
              onChangeText={(value) => updateMod(index, { notes: value })}
              placeholder="Notes"
              placeholderTextColor={colors.mutedText}
              style={[styles.input, styles.modNotes]}
              value={mod.notes ?? ''}
            />
            <Button
              title="Remove mod"
              variant="secondary"
              onPress={() => removeMod(index)}
            />
          </View>
        ))}
      </View>

      {!vehicle ? (
        <View style={styles.shareRow}>
          <View style={styles.shareText}>
            <Text style={styles.label}>Share to feed</Text>
            <Text style={styles.helperText}>
              Create a public feed post with a Garage card after saving.
            </Text>
          </View>
          <Switch
            ios_backgroundColor={colors.border}
            onValueChange={setShareToFeed}
            thumbColor={colors.surface}
            trackColor={{
              false: colors.border,
              true: colors.primary,
            }}
            value={shareToFeed}
          />
        </View>
      ) : null}

      <View style={styles.actions}>
        {vehicle ? (
          <Button
            title="Open public page"
            variant="secondary"
            onPress={() => {
              const path = garageVehiclePath(vehicle);

              if (path) {
                router.push(path);
              }
            }}
          />
        ) : null}
        <Button
          title={vehicle ? 'Save vehicle' : 'Add vehicle'}
          loading={saving}
          disabled={Boolean(uploadStatus)}
          onPress={() => {
            void saveVehicle();
          }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    gap: spacing.lg,
    paddingBottom: spacing.xl,
  },
  section: {
    gap: spacing.md,
  },
  title: {
    color: colors.text,
    fontSize: 28,
    fontWeight: '900',
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  helperText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  segmentGroup: {
    gap: spacing.sm,
  },
  segmentRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  segment: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  segmentActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  segmentText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  segmentTextActive: {
    color: colors.surface,
  },
  formGrid: {
    gap: spacing.md,
  },
  field: {
    gap: spacing.xs,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  input: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    fontSize: 16,
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  textArea: {
    minHeight: 140,
  },
  countText: {
    color: colors.mutedText,
    fontSize: 12,
    textAlign: 'right',
  },
  sectionHeader: {
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  sectionHeaderButton: {
    alignSelf: 'flex-start',
  },
  uploadStatus: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
  },
  uploadStatusText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '800',
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  photoCard: {
    gap: spacing.xs,
    width: 132,
  },
  photoFrame: {
    aspectRatio: 1,
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  photoFrameSelected: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
  photoImage: {
    height: '100%',
    width: '100%',
  },
  coverBadge: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    bottom: spacing.xs,
    left: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    position: 'absolute',
  },
  coverBadgeText: {
    color: colors.surface,
    fontSize: 11,
    fontWeight: '900',
  },
  photoActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  iconButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  modCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  modNotes: {
    minHeight: 72,
  },
  shareRow: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
  },
  shareText: {
    flex: 1,
    gap: spacing.xs,
  },
  actions: {
    gap: spacing.md,
  },
});
